import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { announcementApi } from '@/api';
import { Button, Icon, Modal } from '@/components/ui';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';

/** Announcements change rarely; a slow poll is enough. */
const POLL_MS = 5 * 60_000;

const DISMISSED_KEY = 'jw.announcements.dismissed';

/**
 * Which announcements this browser has closed, by id.
 *
 * A convenience, not a record: storage can be missing or throw (private
 * windows, blocked site data), and then a closed banner simply comes back on
 * the next visit.
 */
function readDismissed() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(DISMISSED_KEY) ?? '[]');
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids) {
  try {
    // Only the most recent few; an old id is never shown again anyway.
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-50)));
  } catch {
    /* it just will not survive a reload */
  }
}

/** Where a link goes, said in words: "a page on Join-Wonder" or the site's name. */
function linkTarget(href) {
  if (href.startsWith('/')) return null;
  try {
    return new URL(href).hostname.replace(/^www\./, '');
  } catch {
    return null;
  }
}

function OpenLink({ href, className }) {
  const site = linkTarget(href);
  const label = site ? `Open ${site}` : 'Open page';
  if (href.startsWith('/')) {
    return (
      <Link to={href} className={className}>
        {label}
        <Icon name="arrowRight" size={14} />
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
      {label}
      <Icon name="external" size={14} />
    </a>
  );
}

const LINK_BUTTON =
  'bg-brand-600 hover:bg-brand-700 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold whitespace-nowrap text-white shadow-xs transition';

/** The whole announcement: message in full, when it was posted, until when, and its link. */
function AnnouncementDetails({ item, onClose, onDismiss }) {
  return (
    <Modal
      open
      title={item.title}
      description={`Announcement from Team Join-Wonder · ${formatDate(item.starts_at)}`}
      onClose={onClose}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onDismiss}>
            Don&apos;t show again
          </Button>
          {item.link ? (
            <OpenLink href={item.link} className={cn(LINK_BUTTON, 'justify-center py-2')} />
          ) : (
            <Button onClick={onClose}>Got it</Button>
          )}
        </div>
      }
    >
      <p className="text-ink text-sm leading-relaxed break-words whitespace-pre-line">
        {item.body}
      </p>
      <dl className="border-line bg-surface-muted mt-4 grid gap-2 rounded-lg border px-3 py-2.5 text-xs sm:grid-cols-2">
        <div>
          <dt className="text-ink-muted">Posted</dt>
          <dd className="text-ink font-medium">{formatDate(item.starts_at)}</dd>
        </div>
        <div>
          <dt className="text-ink-muted">Shown until</dt>
          <dd className="text-ink font-medium">
            {item.ends_at ? formatDate(item.ends_at) : 'Further notice'}
          </dd>
        </div>
        {item.link && (
          <div className="sm:col-span-2">
            <dt className="text-ink-muted">Link</dt>
            <dd className="text-ink font-medium break-all">{item.link}</dd>
          </div>
        )}
      </dl>
    </Modal>
  );
}

/**
 * Platform announcements for the signed-in user, under the top bar.
 *
 * The server already picks what is live for this person's audience; this only
 * hides the ones they closed. On an error it shows nothing -- a banner is never
 * worth an error message. The banner says what it is and when it was posted,
 * shows the message, and "View details" opens all of it.
 */
export function AnnouncementBanner() {
  const [dismissed, setDismissed] = useState(readDismissed);
  const [openId, setOpenId] = useState(null);

  const { data } = useQuery({
    queryKey: ['announcements', 'live'],
    queryFn: announcementApi.listLive,
    refetchInterval: POLL_MS,
    staleTime: POLL_MS,
    retry: false,
  });

  const visible = (data ?? []).filter((item) => !dismissed.includes(item.id));
  if (visible.length === 0) return null;
  const open = visible.find((item) => item.id === openId);

  function dismiss(id) {
    const next = [...dismissed, id];
    setDismissed(next);
    writeDismissed(next);
    setOpenId(null);
  }

  return (
    <div className="mb-6 space-y-3">
      {visible.map((item) => (
        <section
          key={item.id}
          aria-label={`Announcement: ${item.title}`}
          className="border-brand-200 bg-surface relative flex items-start gap-3 overflow-hidden rounded-2xl border py-3.5 pr-3 pl-5 shadow-xs"
        >
          <span aria-hidden="true" className="bg-brand-500 absolute inset-y-0 left-0 w-1" />
          <span
            aria-hidden="true"
            className="bg-brand-50 text-brand-700 grid size-10 shrink-0 place-items-center rounded-xl"
          >
            <Icon name="bell" size={18} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
              <span className="bg-brand-100 text-brand-700 rounded-full px-2 py-0.5 font-semibold tracking-wide uppercase">
                Announcement
              </span>
              <span className="text-ink-muted">
                From Team Join-Wonder · {formatDate(item.starts_at)}
              </span>
            </p>
            <h2 className="text-ink mt-1 text-[15px] font-semibold">{item.title}</h2>
            <p className="text-ink-muted mt-0.5 line-clamp-3 text-sm leading-relaxed break-words whitespace-pre-line">
              {item.body}
            </p>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setOpenId(item.id)}
                className="border-brand-200 text-brand-700 hover:bg-brand-50 inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition"
              >
                <Icon name="eye" size={14} />
                View details
              </button>
              {item.link && <OpenLink href={item.link} className={LINK_BUTTON} />}
            </div>
          </div>
          <button
            type="button"
            onClick={() => dismiss(item.id)}
            aria-label={`Dismiss ${item.title}`}
            title="Dismiss"
            className="text-ink-muted hover:bg-brand-50 hover:text-brand-700 shrink-0 rounded-lg p-1.5 transition"
          >
            <Icon name="close" size={16} />
          </button>
        </section>
      ))}
      {open && (
        <AnnouncementDetails
          item={open}
          onClose={() => setOpenId(null)}
          onDismiss={() => dismiss(open.id)}
        />
      )}
    </div>
  );
}
